import React, { useContext, useEffect, useState } from "react";
import { useTheme } from "emotion-theming";
import { useHistory } from "react-router-dom";

import { AddressContext } from "wrapper/AddressWrapper/context/addressContext";
import { SavedAddressContext } from "wrapper/SavedAddressWrapper/context/savedAddressContext";

import { FilledButton } from "@nykaa/payment-ui/src/Button";
import { BottomSheet } from "@nykaa/payment-ui/src/BottomSheet/BottomSheet/index";
import { Title } from "@nykaa/payment-ui/src/Title";
import { Image } from "@nykaa/payment-ui/src/Image";

import { PaymentSeo } from "layouts/PaymentSeo";
import { Wrapper, Main } from "./assets/style";
import { AddressCard } from "./components/AddressCard";
import { OverlayLoader } from "components/OverlayLoader";
import { PincodeBottomSheetContent } from "../NewAddress/components/PincodeBottomSheet";
import { SelectedAddressLoader } from "layouts/CheckoutPayment/mobile/ShippingAddress/common/selectedAddressLoader";

import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getPlatform } from "../../helpers/index";

import MobHeader from "components/NewMobHeader";

/**
 * Saved Address of user is displayed
 * loading state and error modal is also handled from config
 * @param config {SavedAddressConfig} - config object that is needed to be passed
 * @returns
 * view part containing all the addresses
 */
const SavedAddress = () => {
  const savedAddressContext: any = useContext(SavedAddressContext);
  const addressContext = useContext(AddressContext);
  const history = useHistory();
  const addresses = savedAddressContext?.addresses;
  const loading = savedAddressContext?.loading;
  const selectedAddress = savedAddressContext?.selectedAddress;
  const handleShipAddressClick = savedAddressContext?.handleShipAddressClick;
  const handleAddAddressClick = savedAddressContext?.handleAddAddressClick;
  const handleEditClick = savedAddressContext?.handleEditClick;
  const handleCheckBoxClick = savedAddressContext?.handleCheckBoxClick;
  const checkedId = savedAddressContext?.checkedId;
  const isDefaultAddress = savedAddressContext?.isDefaultAddress;
  const vernacularMessages = savedAddressContext?.vernacularMessages;
  const handleBackButtonClick = savedAddressContext?.handleBackButtonClick;
  const onPincodeBottomSheetClose = savedAddressContext?.onPincodeBottomSheetClose;
  const isPincodeBottomSheetVisible = savedAddressContext?.isPincodeBottomSheetVisible;
  const pincodeBottomSheetTitle = savedAddressContext?.pincodeBottomSheetTitle;
  const pincodeBottomSheetSubTitle = savedAddressContext?.pincodeBottomSheetSubTitle;
  const pincodeBottomSheetActionButtonText =
    savedAddressContext?.pincodeBottomSheetActionButtonText;
  const platform = getPlatform();
  const [savedAddressLoading, setSavedAddressLoading] = useState(true);

  const {
    headerTitle,
    addNewAddress,
    defaultTitle,
    editTitle,
    shipToThisAddress,
    footerTitle1,
    footerTitle2,
  } = vernacularMessages;

  const theme: Theme = useTheme();

  useEffect(() => {
    setSavedAddressLoading(false);
  }, []);

  if (addressContext?.addressLoading || savedAddressLoading) {
    return <SelectedAddressLoader />;
  }

  return (
    <>
      <PaymentSeo
        title={__SEO_TITLE__}
        canonicalLink={__CANONICAL_LINK__}
        description={__SEO_DESCRIPTION__}
        img={__LOGO__}
      />
      <Wrapper>
        <MobHeader
          title={headerTitle}
          onIconClick={handleBackButtonClick}
          fixAtTop
          dataTestId="lb_selectAddress"
        />
        <Main className="layout vertical pt15">
          <MobHeader
            title={addNewAddress}
            icon={
              <Image
                url={getIcons(platform, ICONS_NAMES.ADD_ADDRESS_ICON)}
                alt={"error"}
                theme={theme}
              />
            }
            color={theme.brandtextPrimaryColor}
            extraClass="add-address"
            onClick={handleAddAddressClick}
            onIconClick={handleAddAddressClick}
            dataTestId="add_new_address_btn"
          />
          <div className="layout vertical justified" data-testid="saved_address_list">
            {addresses?.map((address: AddressDataType, index: number) => {
              // setting checked Id as default address id for the first time
              const isCheckedAddress = !!(checkedId === address.customerAddressId);
              return (
                <AddressCard
                  isDefault={address.isSetDefault ? isDefaultAddress : false}
                  // tag={}
                  isChecked={isCheckedAddress}
                  address={address}
                  key={index}
                  handleCheckBoxClick={handleCheckBoxClick}
                  handleEditClick={handleEditClick}
                  defaultTitle={defaultTitle}
                  editTitle={editTitle}
                />
              );
            })}
          </div>
          <div
            className="layout horizontal ptb15 prl15 around-justified center-center footer"
            style={{ color: "rgba(0,19,37,0.92)" }}
          >
            {footerTitle1}
            <br />
            {footerTitle2}
            <img
              src={getIcons(__PLATFORM__, ICONS_NAMES.BUYERS_ASSURANCE_ICON)}
              alt="buyer assurance"
            ></img>
          </div>
          <div className="layout vertical bottom-fixed">
            <div className="ptb16">
              <FilledButton
                text={shipToThisAddress}
                onClick={() => {
                  handleShipAddressClick(selectedAddress);
                }}
                theme={theme}
                dataTestId="button_shipToThisAddress"
              />
            </div>
          </div>
        </Main>
        <BottomSheet
          isVisible={isPincodeBottomSheetVisible}
          icon={""}
          onClose={onPincodeBottomSheetClose}
          onIconClick={onPincodeBottomSheetClose}
          theme={theme}
          history={history}
          maxHeight={85}
          dataTestId="lb_confirm_bottomsheet"
        >
          <>
            <Title
              text={pincodeBottomSheetTitle}
              font={theme.typography.titleLarge}
              theme={theme}
              dataTestId="txt_non_Serviceable_pincode"
            />
            <PincodeBottomSheetContent
              text={pincodeBottomSheetSubTitle}
              dismissButtonText={pincodeBottomSheetActionButtonText}
              onDismiss={onPincodeBottomSheetClose}
              font={theme.typography.bodyMedium}
              dismissButtonDataTestId="btn_got_it"
            />
          </>
        </BottomSheet>
      </Wrapper>
      {loading && <OverlayLoader isActive={loading} zIndex={2} />}
    </>
  );
};

export default SavedAddress;
